<template>
  <div class="nexus-container">
    <v-container fluid fill-height>
      <!-- Back Button -->
      <v-btn icon class="back-button" @click="goBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Page Header -->
      <v-row class="pa-7" justify="center">
        <h1 class="nexus-title">ADD SHIPMENT</h1>
      </v-row>

      <!-- Form Card -->
      <v-row class="fill-height pa-0 ma-0" justify="center" align="start">
        <v-col cols="12" md="11" lg="10" class="pa-0">
          <v-card elevation="0" class="nexus-card">
            <v-card-text class="pa-8">
              <v-overlay :value="loading">
                <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
              </v-overlay>

              <v-form ref="form" v-model="valid">
                <!-- Shipment Details Header -->
                <div class="section-header mb-6">
                  <h2 class="section-title">SHIPMENT DETAILS</h2>
                  <div class="section-divider"></div>
                </div>

                <!-- Row 1: Basic shipment info -->
                <v-row class="mb-4">
                  <v-col cols="12" md="6" lg="3">
                    <v-text-field
                      v-model="shipmentNumber"
                      label="Shipment ID"
                      dense outlined readonly
                      prepend-inner-icon="mdi-numeric"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="6" lg="3">
                    <v-text-field
                      v-model="shipmentOrderNumber"
                      label="Shipment Order Number"
                      dense outlined
                      prepend-inner-icon="mdi-note-text"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="6" lg="3">
                    <v-text-field
                      v-model="poNumber"
                      label="PO Number"
                      dense outlined
                      prepend-inner-icon="mdi-file-document"
                      :rules="[rules.required]"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="6" lg="3">
                    <v-autocomplete
                      v-model="supplierName"
                      :items="supplierOptions"
                      label="Supplier"
                      dense outlined
                      prepend-inner-icon="mdi-factory"
                      :rules="[rules.required]"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Row 2: Dates, method, status -->
                <v-row class="mb-6">
                  <v-col cols="12" md="4">
                    <v-menu
                      ref="arrivalDateMenu"
                      v-model="arrivalDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="arrivalDate"
                          label="Expected Arrival Date"
                          readonly dense outlined
                          prepend-inner-icon="mdi-calendar-clock"
                          v-bind="attrs" v-on="on"
                          :rules="[rules.required]"
                          class="nexus-input"
                        />
                      </template>
                      <v-date-picker
                        v-model="arrivalDate"
                        @input="arrivalDateMenu = false"
                      />
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="method"
                      :items="methodOptions"
                      label="Method"
                      dense outlined
                      prepend-inner-icon="mdi-truck-delivery"
                      :rules="[rules.required]"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="status"
                      :items="statusOptions"
                      label="Status"
                      dense outlined
                      prepend-inner-icon="mdi-information"
                      :rules="[rules.required]"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Linked SO Numbers Section -->
                <div class="section-header mb-4">
                  <h3 class="section-title">LINKED SO NUMBERS</h3>
                  <div class="section-divider"></div>
                </div>

                <v-row class="mb-4">
                  <v-col cols="12" md="8">
                    <v-autocomplete
                      v-model="selectedSOs"
                      :items="availableSOs"
                      item-text="soDisplay"
                      item-value="salesOrderNumber"
                      label="Select SO Numbers"
                      multiple
                      chips
                      dense outlined
                      prepend-inner-icon="mdi-format-list-numbered"
                      :rules="[rules.requiredArray]"
                      class="nexus-input"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip
                          v-if="index < 3"
                          :key="item.salesOrderNumber"
                          color="primary"
                          dark
                          small
                          close
                          @click:close="removeSO(item.salesOrderNumber)"
                          class="nexus-chip"
                        >
                          SO: {{ item.salesOrderNumber }}
                        </v-chip>
                        <span
                          v-if="index === 3"
                          class="grey--text text-caption"
                        >
                          (+{{ selectedSOs.length - 3 }} others)
                        </span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>

                <!-- SO Details Table -->
                <v-row v-if="selectedSOs.length" class="mb-6">
                  <v-col cols="12">
                    <div class="section-header mb-3">
                      <h4 class="section-title" style="font-size: 1.1rem;">SELECTED SO DETAILS</h4>
                      <div class="section-divider"></div>
                    </div>
                    <v-data-table
                      :headers="soTableHeaders"
                      :items="selectedSODetails"
                      class="nexus-table elevation-2"
                      :items-per-page="5"
                      :footer-props="{
                        'items-per-page-options': [5, 10, 15],
                        'show-current-page': true
                      }"
                      no-data-text="No SOs selected"
                    >
                      <template v-slot:item.salesOrderNumber="{ item }">
                        <v-chip color="primary" dark small class="nexus-chip">
                          SO: {{ item.salesOrderNumber }}
                        </v-chip>
                      </template>
                      <template v-slot:item.status="{ item }">
                        <v-chip :color="getStatusColor(item.status)" dark small>
                          {{ item.status }}
                        </v-chip>
                      </template>
                      <template v-slot:item.actions="{ item }">
                        <v-btn
                          icon
                          @click="removeSO(item.salesOrderNumber)"
                          color="error"
                          small
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>

                <!-- Save Button -->
                <v-row justify="center" class="mt-8">
                  <v-btn
                    color="primary"
                    large
                    width="300px"
                    @click="confirmSubmit"
                    :disabled="!valid || loading"
                    class="nexus-btn-primary"
                  >
                    <v-icon left>mdi-content-save</v-icon>Save Shipment
                  </v-btn>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmationDialog.visible" max-width="400">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">{{ confirmationDialog.title }}</v-card-title>
        <v-card-text>{{ confirmationDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="confirmationDialog.confirm">
            <v-icon left>mdi-check</v-icon>Yes
          </v-btn>
          <v-btn color="grey" text @click="confirmationDialog.visible = false">
            <v-icon left>mdi-close</v-icon>No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbars -->
    <v-snackbar v-model="successSnackbar" color="success" timeout="3000">
      {{ successMessage }}
      <v-btn color="white" text @click="successSnackbar = false">Close</v-btn>
    </v-snackbar>

    <v-snackbar v-model="errorSnackbar" color="error" timeout="3000">
      {{ errorMessage }}
      <v-btn color="white" text @click="errorSnackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore, collection, addDoc, getDocs, 
  query, where, updateDoc, doc, getDoc, runTransaction
} from "firebase/firestore";
import { firebaseApp, auth } from "@/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

const db = getFirestore(firebaseApp);

export default {
  name: "AddShipment",
  data() {
    return {
      loading: false,
      valid: false,
      shipmentNumber: "1",
      shipmentOrderNumber: "",
      poNumber: "",
      supplierName: "",
      arrivalDate: "",
      arrivalDateMenu: false,
      method: "",
      status: "",
      selectedSOs: [],
      authStateUnsubscribe: null,
      currentUser: null,
      
      supplierOptions: [],
      availableSOs: [],
      
      methodOptions: ["Air", "Sea", "Road"],
      statusOptions: [
        "New Order", "Manufacturing", "Booked", "In Transit", 
        "Docked", "At Airport", "On Route to JHB", "Delivered"
      ],
      
      confirmationDialog: { 
        visible: false, title: "", message: "", confirm: () => {} 
      },
      successSnackbar: false,
      successMessage: "",
      errorSnackbar: false,
      errorMessage: "",
      
      rules: { 
        required: (v) => !!v || "Required.",
        requiredArray: (v) => (v && v.length > 0) || "At least one SO must be selected."
      },
      
      soTableHeaders: [
        { text: "SO Number", value: "salesOrderNumber", width: "140px" },
        { text: "Supplier", value: "supplierName", width: "200px" },
        { text: "Status", value: "status", width: "150px" },
        { text: "Actions", value: "actions", sortable: false, width: "80px", align: "center" },
      ],
    };
  },
  
  computed: {
    selectedSODetails() {
      return this.selectedSOs.map(soNumber => {
        const so = this.availableSOs.find(s => s.salesOrderNumber === soNumber);
        return {
          salesOrderNumber: soNumber,
          supplierName: so ? so.supplierName : 'Unknown Supplier',
          customerName: so ? so.customerName : 'Unknown Customer',
          status: so ? so.status : 'Unknown Status'
        };
      });
    }
  },
  
  async created() {
    this.loading = true;
    
    // Setup auth state listener
    this.authStateUnsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        this.$router.push('/login');
        return;
      }
      
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          this.currentUser = data.name || user.email || "Unknown User";
        }
      } catch (e) {
        this.handleError(e, "Loading user data");
      }
    });
    
    try {
      await Promise.all([
        this.fetchSupplierOptions(),
        this.fetchAvailableSOs(),
        this.getShipmentNumber()
      ]);
    } catch (e) {
      this.handleError(e, "Initializing form");
    } finally {
      this.loading = false;
    }
  },

  beforeDestroy() {
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
    }
  },
  
  methods: {
    handleError(error, operation) {
      console.error(`Error in ${operation}:`, error);
      
      if (error.code === 'permission-denied' || error.code === 'unauthenticated') {
        this.$router.push('/login');
        return;
      }
      
      this.errorMessage = `${operation} failed: ${error.message}`;
      this.errorSnackbar = true;
    },

    async fetchSupplierOptions() {
      try {
        const supplierCollection = collection(db, "suppliers");
        const supplierSnapshot = await getDocs(supplierCollection);
        
        this.supplierOptions = supplierSnapshot.docs.map(doc => {
          const data = doc.data();
          
          const supplierName = data["Supplier Name"] || 
                              data.supplierName || 
                              data.name || 
                              data.companyName || 
                              data.supplier || 
                              data.company ||
                              data.businessName ||
                              data.vendorName ||
                              data.organisationName ||
                              data.title ||
                              data.label ||
                              "Unknown Supplier";
          
          return supplierName;
        }).sort((a, b) => a.localeCompare(b));
        
      } catch (e) {
        this.handleError(e, "Loading suppliers");
        this.supplierOptions = ["Test Supplier 1", "Test Supplier 2", "Test Supplier 3"];
      }
    },

    async fetchAvailableSOs() {
      try {
        const soCollection = collection(db, "SalesTracker");
        const soSnapshot = await getDocs(soCollection);
        
        this.availableSOs = [];
        
        soSnapshot.docs.forEach(doc => {
          const data = doc.data();
          
          // Skip delivered SOs
          if (data.status?.toLowerCase() === "delivered") {
            return;
          }
          
          this.availableSOs.push({
            salesOrderNumber: data.salesOrderNumber || "Unknown",
            supplierName: data.supplierName || "Unknown Supplier",
            customerName: data.customerName || "Unknown Customer",
            status: data.status || "Unknown Status",
            soDisplay: `SO: ${data.salesOrderNumber || "Unknown"} - ${data.customerName || "Unknown Customer"} (${data.status || "Unknown Status"})`,
            docId: doc.id
          });
        });
        
        // Sort SOs by salesOrderNumber in descending order (newest to oldest)
        this.availableSOs.sort((a, b) => {
          const aNum = parseInt(a.salesOrderNumber) || 0;
          const bNum = parseInt(b.salesOrderNumber) || 0;
          return bNum - aNum; // Descending order (newest first)
        });
        
      } catch (e) {
        this.handleError(e, "Loading available SOs");
      }
    },

    async getShipmentNumber() {
      try {
        const shipmentCollection = collection(db, "shipments");
        const shipmentSnapshot = await getDocs(shipmentCollection);
        
        const nextNumber = shipmentSnapshot.docs.length + 1;
        this.shipmentNumber = nextNumber.toString();
        
      } catch (e) {
        this.handleError(e, "Generating shipment number");
        this.shipmentNumber = "1";
      }
    },

    removeSO(soNumber) {
      this.selectedSOs = this.selectedSOs.filter(so => so !== soNumber);
    },

    getStatusColor(status) {
      const colors = {
        'Open Order': 'blue',
        'Manufacturing': 'amber',
        'Booked': 'orange',
        'Partial Supply': 'deep-orange',
        'In Transit': 'green',
        'Docked': 'teal',
        'At Airport': 'cyan',
        'On Route to JHB': 'light-green',
        'Delivered': 'success',
        'Complete': 'success',
        'Cancelled': 'error'
      };
      return colors[status] || 'grey';
    },

    confirmSubmit() {
      if (this.selectedSOs.length === 0) {
        this.errorMessage = "Please select at least one SO.";
        this.errorSnackbar = true;
        return;
      }
      
      this.confirmationDialog = {
        visible: true,
        title: "Confirm Save",
        message: "Are you sure you want to save this shipment?",
        confirm: this.submitForm,
      };
    },

    async submitForm() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      
      try {
        const shipmentData = {
          shipmentNumber: this.shipmentNumber,
          shipmentOrderNumber: this.shipmentOrderNumber,
          poNumber: this.poNumber,
          supplierName: this.supplierName,
          expectedArrivalDate: this.arrivalDate,
          method: this.method,
          status: this.status,
          linkedSONumbers: this.selectedSOs,
          createdDate: new Date().toISOString(),
          lastModified: new Date().toISOString()
        };

        // Use transaction to ensure data consistency
        await runTransaction(db, async (transaction) => {
          // Add the shipment
          const shipmentRef = doc(collection(db, "shipments"));
          transaction.set(shipmentRef, shipmentData);
        });

        // After transaction completes, update SO statuses and remove from Open Order
        await this.updateLinkedSOStatusesInDB();
        await this.removeSOsFromOpenOrder(this.selectedSOs);

        this.successMessage = `Shipment #${this.shipmentNumber} added successfully!`;
        this.successSnackbar = true;
        
        // Reset form
        this.$refs.form.reset();
        this.selectedSOs = [];
        
        // Navigate back after delay
        setTimeout(() => {
          this.$router.go(-1);
        }, 1500);
        
      } catch (e) {
        this.handleError(e, "Saving shipment");
      } finally {
        this.loading = false;
        this.confirmationDialog.visible = false;
      }
    },

    async updateLinkedSOStatusesInDB() {
      try {
        for (const soNumber of this.selectedSOs) {
          const soDoc = this.availableSOs.find(so => so.salesOrderNumber === soNumber);
          if (soDoc) {
            const shipmentStatuses = await this.getSOShipmentStatuses(soNumber);
            let newStatus;
            
            if (shipmentStatuses.length > 1) {
              // SO is on multiple shipments
              const allDelivered = shipmentStatuses.every(s => s === "Delivered");
              newStatus = allDelivered ? "Delivered" : "Partial Supply";
            } else if (shipmentStatuses.length === 1) {
              // SO is on only one shipment
              newStatus = this.status === "Delivered" ? "Delivered" : this.status;
            } else {
              newStatus = "Open Order";
            }
            
            await runTransaction(db, async (transaction) => {
              const soDocRef = doc(db, "SalesTracker", soDoc.docId);
              const soDocSnap = await transaction.get(soDocRef);
              if (soDocSnap.exists()) {
                const soData = soDocSnap.data();
                const supplierUpdates = soData.supplierUpdates || [];
                
                const statusUpdate = {
                  text: `Status updated to "${newStatus}" from Shipment #${this.shipmentNumber}`,
                  timestamp: new Date().toISOString(),
                  user: this.currentUser || "System",
                  source: 'shipment_creation',
                  shipmentNumber: this.shipmentNumber
                };
                
                supplierUpdates.push(statusUpdate);
                
                transaction.update(soDocRef, {
                  status: newStatus,
                  supplierUpdates: supplierUpdates
                });
              }
            });
          }
        }
      } catch (e) {
        this.handleError(e, "Updating linked SO statuses");
      }
    },

    async getSOShipmentStatuses(soNumber) {
      try {
        const shipmentsCol = collection(db, "shipments");
        const allShipmentsSnapshot = await getDocs(shipmentsCol);
        
        const statuses = [];
        
        allShipmentsSnapshot.forEach(doc => {
          const data = doc.data();
          const linkedSOs = data.linkedSONumbers || [];
          
          if (data.shipmentNumber !== "OPEN_ORDER" && linkedSOs.includes(soNumber)) {
            statuses.push(data.status);
          }
        });
        
        return statuses;
      } catch (error) {
        this.handleError(error, "Getting SO shipment statuses");
        return [];
      }
    },

    async removeSOsFromOpenOrder(soNumbers) {
      try {
        const openOrderQuery = query(
          collection(db, 'shipments'), 
          where('shipmentNumber', '==', 'OPEN_ORDER')
        );
        const snapshot = await getDocs(openOrderQuery);
        
        if (!snapshot.empty) {
          const openOrderDoc = snapshot.docs[0];
          const currentLinkedSOs = openOrderDoc.data().linkedSONumbers || [];
          
          // Remove the SOs that are now on this shipment
          const updatedLinkedSOs = currentLinkedSOs.filter(
            so => !soNumbers.includes(so)
          );
          
          await updateDoc(openOrderDoc.ref, {
            linkedSONumbers: updatedLinkedSOs
          });
        }
      } catch (error) {
        this.handleError(error, "Removing SOs from Open Order");
      }
    },

    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");

.nexus-container {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
}

.nexus-title {
  color: white;
  font-weight: 700;
  font-size: 2.5rem;
  text-align: center;
  letter-spacing: 2px;
  margin: 0;
}

.nexus-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  margin-top: 20px;
  transition: all 0.3s ease;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.section-title {
  color: #475569;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: 1px;
  margin: 0;
  white-space: nowrap;
}

.section-divider {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #475569 0%, rgba(71, 85, 105, 0.2) 100%);
  margin-left: 20px;
  border-radius: 1px;
}

.nexus-input >>> .v-input__control {
  border-radius: 12px;
}

.nexus-input >>> .v-input__slot {
  background: rgba(248, 250, 252, 0.8) !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.nexus-input >>> .v-input__slot:hover {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

.nexus-input >>> .v-input--is-focused .v-input__slot {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.15);
}

.nexus-btn-primary {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 16px !important;
  font-weight: 700 !important;
  letter-spacing: 1px !important;
  text-transform: uppercase !important;
  font-size: 1.1rem !important;
  padding: 16px 32px !important;
  box-shadow: 
    0 10px 30px 0 rgba(71, 85, 105, 0.4),
    0 0 0 1px rgba(71, 85, 105, 0.2) !important;
  transition: all 0.3s ease !important;
}

.nexus-btn-primary:hover {
  transform: translateY(-3px) !important;
  box-shadow: 
    0 15px 40px 0 rgba(71, 85, 105, 0.5),
    0 0 0 1px rgba(71, 85, 105, 0.3) !important;
}

.nexus-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
}

.nexus-table {
  border-radius: 16px !important;
  overflow: hidden !important;
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px) !important;
}

.nexus-table >>> .v-data-table__wrapper {
  border-radius: 16px !important;
}

.nexus-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
}

.nexus-table >>> .v-data-table-header th {
  color: #334155 !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e2e8f0 !important;
}

.nexus-dialog {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px) !important;
}

.nexus-dialog-title {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #1e293b !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.back-button {
  color: white !important;
  background: rgba(71, 85, 105, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 50% !important;
  position: absolute;
  top: 24px;
  left: 24px;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.4) !important;
}

.back-button:hover {
  transform: scale(1.1) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.5) !important;
}

.back-button .v-icon {
  color: white !important;
}

/* Form validation styles */
.nexus-input >>> .v-messages__message {
  color: #dc2626 !important;
  font-weight: 500 !important;
}

.nexus-input >>> .v-input--has-state .v-input__slot {
  border-color: #dc2626 !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .nexus-title {
    font-size: 2rem;
    letter-spacing: 1px;
  }
  
  .section-title {
    font-size: 1.1rem;
  }
  
  .nexus-card {
    margin: 10px;
    border-radius: 16px;
  }
  
  .nexus-card >>> .v-card__text {
    padding: 20px !important;
  }
}
</style>